import { memo } from 'react'
import { PrivateChatHeader } from '../components/PrivateChatHeader'
import { PublicChat } from './PublicChat'
import { PrivateChat } from './PrivateChat'
import type { SharedDiceRollingState } from '../GameControls/useDiceControls'
import type { Message, PrivateMessage, ChatMode } from '@/types/message'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'

interface RespondingAsNpcState {
  isActive: boolean
  npcUserId: string | null
  npcName: string | null
}

interface ChatContainerProps {
  chatMode: ChatMode
  currentUserId?: string

  // Props para PublicChat
  messages: Message[]
  characters: Character[]
  members: MemberType[]
  isMaster: boolean
  sharedDiceRolling: SharedDiceRollingState
  /** A bandeja unica esta na tela de todos: a cerimonia nao pode pular por
   *  cima dela (fase A do redesenho — a jogada acontece NA bandeja). */
  cerimoniaSuprimida?: boolean
  /** Envio da rolagem congelada na cerimonia (so quem rolou ve o botao). */
  onEnviarRolagem?: (rollId: string) => Promise<boolean>
  loadingHistory: boolean
  onUpdateMessage?: (messageId: string, content: string) => Promise<void>

  // Props para PrivateChat
  privateMessages: PrivateMessage[]
  selectedPrivateUser: MemberType | null
  loadingPrivateHistory: boolean
  respondingAsNpc: RespondingAsNpcState
  isUserOnline: (userId: string) => boolean
  onBackToGroup: () => void

  // Paginação (apenas para chat público por enquanto)
  onLoadMore?: () => Promise<void>
  loadingMore?: boolean
  hasMore?: boolean
}

export const ChatContainer = memo(function ChatContainer({
  chatMode,
  currentUserId,
  messages,
  characters,
  members,
  isMaster,
  sharedDiceRolling,
  cerimoniaSuprimida = false,
  onEnviarRolagem,
  loadingHistory,
  onUpdateMessage,
  privateMessages,
  selectedPrivateUser,
  loadingPrivateHistory,
  respondingAsNpc,
  isUserOnline,
  onBackToGroup,
  onLoadMore,
  loadingMore,
  hasMore
}: ChatContainerProps) {
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header específico para chat privado */}
      {chatMode === 'private' && selectedPrivateUser && (
        <div className="flex-shrink-0">
          <PrivateChatHeader
            member={selectedPrivateUser}
            isOnline={isUserOnline(selectedPrivateUser.userId)}
            onBackToGroup={onBackToGroup}
            respondingAsNpc={respondingAsNpc}
          />
        </div>
      )}
      
      {/* Renderizar chat público ou privado */}
      {chatMode === 'group' ? (
        <PublicChat
          messages={messages}
          characters={characters}
          members={members}
          isMaster={isMaster}
          currentUserId={currentUserId || ''}
          sharedDiceRolling={sharedDiceRolling}
          cerimoniaSuprimida={cerimoniaSuprimida}
          onEnviarRolagem={onEnviarRolagem}
          loadingHistory={loadingHistory}
          onUpdateMessage={onUpdateMessage}
          onLoadMore={onLoadMore}
          loadingMore={loadingMore}
          hasMore={hasMore}
        />
      ) : (
        <PrivateChat
          messages={privateMessages}
          characters={characters}
          members={members}
          currentUserId={currentUserId || ''}
          selectedPrivateUser={selectedPrivateUser}
          sharedDiceRolling={sharedDiceRolling}
          cerimoniaSuprimida={cerimoniaSuprimida}
          onEnviarRolagem={onEnviarRolagem}
          loadingHistory={loadingPrivateHistory}
        />
      )}
    </div>
  )
})